// Вызов инструмента и его вывод — консольной схемой claude code:
//
//   ⏺ Bash(ls -la /home)
//     ⎿  total 24
//        drwxr-xr-x  4 ubuntu ubuntu 4096 Sep  9 10:50 .
//        … +12 строк
//
// Имя инструмента жирным, аргумент в скобках, вывод — под «⎿» с отступом в
// пять знаков. Раскрытие вывода — состояние КОНКРЕТНОЙ строки
// (`${sessionId}:${seq}`), живёт в сторе и переживает добавление новых строк
// (FR-16). Разворот ничего не перезапрашивает: rows уже пришли стримом.

import { memo, useEffect, useState } from 'react'
import { DEFAULT_GUTTER } from '../protocol'
import { useExpanded, useLogStore, usePane } from '../store/logStore'
import type { LogLine } from '../store/logStore'

interface Props {
  sessionId: string
  line: LogLine
}

/** Сколько строк вывода показывать до «… +N строк». Протокол обещает 2..4. */
function headCount(head: number | null | undefined): number {
  if (typeof head !== 'number' || !Number.isFinite(head)) return 3
  return Math.max(1, Math.min(20, Math.floor(head)))
}

/** Дальше этого числа строк результат уже точно пришёл (или не придёт) — не ищем. */
const RUNNING_LOOKBACK = 300

/**
 * «⎿ выполняется… 12 с» под вызовом, пока у него нет результата, — как
 * «Running…» в консоли. Без этого долгий вызов (Bash, MCP вроде Yonote)
 * выглядел как зависание: строка вызова есть, а что происходит — непонятно.
 */
function Running({ since }: { since: number }) {
  const [now, setNow] = useState(() => Date.now() / 1000)
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now() / 1000), 1000)
    return () => window.clearInterval(id)
  }, [])
  const sec = Math.max(0, Math.floor(now - since))
  const label = sec < 60 ? `${sec} с` : `${Math.floor(sec / 60)} мин ${sec % 60} с`
  return (
    <div className="t-res t-res--running">
      <span className="t-gut t-gut--res">⎿</span>
      <div className="t-rows">
        <div className="t-rowline">выполняется… {label}</div>
      </div>
    </div>
  )
}

function ToolBlock({ sessionId, line }: Props) {
  const waiting = usePane(sessionId, (pane) => {
    if (line.kind !== 'tool' || !line.toolUseId || line.partial) return false
    const state = pane.session?.state
    if (state !== 'running' && state !== 'waiting_permission') return false
    const lines = pane.lines
    const last = lines[lines.length - 1]
    if (!last || last.seq - line.seq > RUNNING_LOOKBACK) return false
    for (let i = lines.length - 1; i >= 0; i--) {
      const l = lines[i]
      if (l.seq <= line.seq) break
      if (l.toolUseId === line.toolUseId) return false
    }
    return true
  })
  const expanded = useExpanded(sessionId, line.seq)
  const toggleExpanded = useLogStore((s) => s.toggleExpanded)

  if (line.kind === 'tool') {
    const glyph = line.gutter ?? DEFAULT_GUTTER.tool
    // Первая строка аргумента идёт в скобки к имени, как в консоли; всё
    // остальное (многострочная команда, тело файла) — ниже тем же отступом.
    const arg = line.text ?? ''
    const cut = arg.indexOf('\n')
    const first = cut === -1 ? arg : arg.slice(0, cut)
    const rest = cut === -1 ? '' : arg.slice(cut + 1)
    return (
      <div className="t-row t-row--head t-row--call">
        <span className="t-gut t-gut--call">{glyph}</span>
        <span className="t-txt">
          {line.tool ? <b className="t-toolname">{line.tool}</b> : null}
          {line.tool ? '(' : null}
          <span className="t-arg">{first}</span>
          {line.tool ? ')' : null}
          {rest ? <span className="t-argrest">{`\n${rest}`}</span> : null}
          {line.partial ? <i className="t-caret" /> : null}
        </span>
        {waiting ? <Running since={line.ts} /> : null}
      </div>
    )
  }

  const rows = line.rows ?? []
  const head = headCount(line.head)
  const hidden = Math.max(0, rows.length - head)
  const shown = expanded ? rows : rows.slice(0, head)
  const glyph = line.gutter ?? DEFAULT_GUTTER.result
  // Ошибка выполнения приходит с kind="warn" — в консоли она красная.
  const tone = line.kind === 'warn' ? ' t-res--err' : ''

  return (
    <div className={`t-res${tone}`}>
      <span className="t-gut t-gut--res">{glyph}</span>
      <div className="t-rows">
        {line.text ? <div className="t-rowline">{line.text}</div> : null}
        {shown.map((row, i) => (
          <div className="t-rowline" key={i}>{row}</div>
        ))}
        {line.partial && shown.length === rows.length ? (
          <div className="t-rowline"><i className="t-caret" /></div>
        ) : null}
        {hidden > 0 || expanded ? (
          <button
            type="button"
            className="t-more"
            onClick={() => toggleExpanded(sessionId, line.seq)}
          >
            {expanded ? '… свернуть' : `… +${hidden} строк`}
          </button>
        ) : null}
      </div>
    </div>
  )
}

export default memo(ToolBlock)
